import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

type Role = 'author' | 'reader';

export function useActiveRole() {
  const { user } = useAuth();
  const [actualRole, setActualRole] = useState<Role | null>(null);
  const [activeRole, setActiveRole] = useState<Role | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUserRole();
    } else {
      setActualRole(null);
      setActiveRole(null);
      setIsAdmin(false);
      setLoading(false);
    }
  }, [user]);

  const fetchUserRole = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user!.id)
        .single();

      // Admin is treated as an author but with additional privileges
      if (profile?.role === 'admin') {
        setIsAdmin(true);
        setActualRole('author');
        // Get active role from localStorage or default to author
        const stored = localStorage.getItem('active_role');
        setActiveRole(stored === 'reader' ? 'reader' : 'author');
      } else if (profile?.role === 'author') {
        setActualRole('author');
        // Get active role from localStorage or default to author
        const stored = localStorage.getItem('active_role');
        setActiveRole(stored === 'reader' ? 'reader' : 'author');
      } else {
        setActualRole('reader');
        setActiveRole('reader');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      setActualRole('reader');
      setActiveRole('reader');
    } finally {
      setLoading(false);
    }
  };

  const toggleRole = (role: Role) => {
    if (actualRole === 'author' || isAdmin) {
      setActiveRole(role);
      localStorage.setItem('active_role', role);
    }
  };

  return {
    actualRole,
    activeRole,
    loading,
    toggleRole,
    isAuthor: actualRole === 'author' || isAdmin,
    isAdmin
  };
}